/* eslint-disable max-len */
import { ChangeEvent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { MESSAGES } from '@app/constant';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLazyGetFaqByIdQuery, useCreateFaqMutation, useUpdateFaqMutation } from '@modules/faq';
import { useRenderFileViewHook } from '@app/hooks/imagesRender';

import { IFlowProcess, IFaqFormData } from './helper/types';
import { createFaqSchema, initialFlowState, initialFormState } from './helper/data';
import { prepareClonedData } from './helper/utils';

export const useFaqLogic = () => {
  const { id } = useParams()

  const [createFaq] = useCreateFaqMutation()
  const [updateFaq] = useUpdateFaqMutation()
  const [getFaqById] = useLazyGetFaqByIdQuery();
  const [flowProcess, setFlowProcess] = useState<IFlowProcess>(initialFlowState)

  const methods = useForm<IFaqFormData>({
    // @ts-ignore
    resolver: yupResolver(createFaqSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods;

  const handleLoadingState = (isLoading: boolean, successMessage: string = '') => {
    setFlowProcess((prev) => ({
      ...prev,
      isButtonLoading: isLoading,
      isSuccess: successMessage,
    }));
  };

  const onSubmit = async (data: IFaqFormData) => {
    handleLoadingState(true);
    try {
      const clonedData = prepareClonedData(data);

      if (id) {
        await updateFaq({ id, body: clonedData }).unwrap()
        getFaqData()
      } else {
        await createFaq(clonedData).unwrap();
        reset(initialFormState);
      }

      handleLoadingState(false, id ? MESSAGES.faqHasBeenSuccessfullyUpdated : MESSAGES.faqHasBeenSuccessfullyCreated);
    } catch (error: any) {
      console.error(error);
      handleLoadingState(false, error?.data?.message || MESSAGES.somethingWentWrong);
    }
  };

  const getFaqData = async () => {
    try {
      if (!id) {
        setFlowProcess((prev) => ({ ...prev, isLoading: false }))

        return
      }

      const faq = await getFaqById(id).unwrap()

      reset({ ...faq });
    } catch (error) {
      console.log("Error", error);
    } finally {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    getFaqData()
  }, [id])

  return {
    ...flowProcess,
    paramId: id,
    methods,
    errors,
    watch,
    setValue,
    setError,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
  }
}
